import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Modal, Box, SelectChangeEvent, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import Selectetat from '../../components/common/SelectEtat'
import UseAlert from '../../components/common/alert'
import {
  getDelaisByActionId,
  deleteDelai,
  updateDelai,
} from '../../redux/delais/delaisActions'
import DelaiCreate from './DelaiCreate'
import { getActionbyId } from '../../redux/actions/actionActions'
import { selectError as selectDelaiError } from '../../redux/delais/delaisSelectors'
import { clearDelaiError } from '../../redux/delais/delaisSlice'
import {
  DetailButton,
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPageDateCell,
  ListPagePill,
  STATUS_PILL,
} from '../../components/common/ListPageCells'
import { locale } from '../../utils/formatNumbers'
import { formatTableDate } from '../../utils/formatDate'
import '../../components/common/ListPageTable.css'

interface DelaiListeProps {
  idaction: string
  delaisSuppSectionRef: React.RefObject<HTMLDivElement | null>
  onDataChange?: () => void
  actionData?: any
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  borderRadius: '12px',
}

const ETAT_PILL: Record<string, { bg: string; color: string }> = {
  ...STATUS_PILL,
  EnCours: { bg: '#E6EFF8', color: '#22548A' },
  Approuvé: { bg: '#E1F4F0', color: '#076B5C' },
  Approuve: { bg: '#E1F4F0', color: '#076B5C' },
  Refusé: { bg: '#FAE6E3', color: '#96271B' },
  Refuse: { bg: '#FAE6E3', color: '#96271B' },
}

const DelaiListe: React.FC<DelaiListeProps> = ({
  idaction,
  onDataChange,
  actionData,
}) => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)
  const delaisList = useSelector((state: RootState) => state.delai.delais)
  const loading = useSelector((state: RootState) => state.delai.loading)
  const apiError = useSelector(selectDelaiError)

  const [openCreate, setOpenCreate] = useState(false)
  const [openEditModal, setOpenEditModal] = useState(false)
  const [selectedRowData, setSelectedRowData] = useState<any>(null)
  const [search, setSearch] = useState('')
  const [Etat, setEtat] = useState<string>('EnCours')
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  useEffect(() => {
    if (idaction) dispatch(getDelaisByActionId(idaction))
  }, [dispatch, idaction])

  useEffect(() => {
    if (apiError) {
      setAlert({ open: true, message: apiError, severity: 'error' })
    }
  }, [apiError])

  const getTranslatedEtat = (etat: string) =>
    t(`tables.AdditionalTimeTable.status.${etat}`, {
      defaultValue: t(etat) || etat,
    })

  const rows = useMemo(() => {
    const list = Array.isArray(delaisList) ? delaisList : []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((r: any) =>
      getTranslatedEtat(r.Etat as string).toLowerCase().includes(q)
    )
  }, [delaisList, search, t])

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }))
    if (alert.severity === 'error') dispatch(clearDelaiError())
  }

  const handleDeleteClick = async (DelaiId: string) => {
    try {
      await dispatch(deleteDelai(DelaiId)).unwrap()
      dispatch(getDelaisByActionId(idaction))
      onDataChange?.()
    } catch (error) {
      console.error('Failed to delete additional time:', error)
    }
  }

  const handleEditClick = (row: any) => {
    setSelectedRowData(row)
    setEtat(row.Etat || 'EnCours')
    setOpenEditModal(true)
  }

  const handleUpdateEtat = async () => {
    try {
      if (selectedRowData?.Etat && selectedRowData?._id) {
        await dispatch(
          updateDelai({
            updatedDelai: {
              DelaiId: selectedRowData.DelaiId,
              DateHeure: selectedRowData.DateHeure,
              Etat,
              DateDebut: selectedRowData.DateDebut,
              DateFin: selectedRowData.DateFin,
              _id: selectedRowData._id,
              ActionId: selectedRowData.ActionId,
            },
            _id: selectedRowData._id,
          })
        ).unwrap()

        setAlert({
          open: true,
          message: t('alerts.delaiUpdate.success'),
          severity: 'success',
        })
        dispatch(getDelaisByActionId(selectedRowData.ActionId))
        dispatch(getActionbyId(selectedRowData.ActionId))
        onDataChange?.()
        setOpenEditModal(false)
      } else {
        setAlert({
          open: true,
          message: t('alerts.delaiUpdate.emptyFields'),
          severity: 'warning',
        })
      }
    } catch (error) {
      console.error('Failed to update additional time state:', error)
    }
  }

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />
      <DetailEntityPanel
        listTitle={t('tables.AdditionalTimeTable.title')}
        searchPlaceholder={t('filterByName', { defaultValue: 'Filter' })}
        searchValue={search}
        onSearchChange={setSearch}
        addLabel={t('buttons.addDemand')}
        onAdd={() => setOpenCreate(true)}
        canAdd={canManageMission(roles)}
      >
        <DetailTable<any>
          loading={loading && !rows.length}
          loadingLabel={t('loading')}
          emptyTitle={t('missionDetailsPage.empty.title', {
            defaultValue: 'Nothing here yet',
          })}
          emptyMessage={t('taskDetailsPage.empty.time', {
            defaultValue: 'No additional time request for this task yet.',
          })}
          rows={rows}
          rowKey={(r) => r._id as string}
          columns={[
            {
              key: 'date',
              header: t(
                'taskDetailsPage.activitiesAccordion.tabs.additionalTime'
              ),
              render: (r) => (
                <ListPageDateCell value={r.Date} locale={locale} />
              ),
            },
            {
              key: 'dateHeure',
              header: t('tables.AdditionalTimeTable.columns.DateHeure'),
              render: (r) => (
                <ListPageDateCell value={r.DateHeure} locale={locale} />
              ),
            },
            {
              key: 'etat',
              header: t('tables.AdditionalTimeTable.columns.Etat'),
              render: (r) => {
                const style = ETAT_PILL[r.Etat] || {
                  bg: '#EAF0F0',
                  color: '#5F7484',
                }
                return (
                  <ListPagePill
                    label={getTranslatedEtat(r.Etat as string)}
                    bg={style.bg}
                    color={style.color}
                  />
                )
              },
            },
          ]}
          actionsHeader={t('tables.AdditionalTimeTable.columns.Action')}
          renderActions={(r) => (
            <DetailRowActions
              roles={roles}
              onEdit={() => handleEditClick(r)}
              onDelete={() => handleDeleteClick(r._id as string)}
              deleteItem={{
                entityType: t(
                  'taskDetailsPage.activitiesAccordion.tabs.additionalTime',
                  { defaultValue: 'Additional Time' }
                ),
                name: r.Date ? formatTableDate(r.Date, locale) : undefined,
                details: [
                  {
                    label: t('deleteConfirm.fields.status'),
                    value: r.Etat || '—',
                  },
                ],
              }}
            />
          )}
        />
      </DetailEntityPanel>

      <Modal open={openCreate} onClose={() => setOpenCreate(false)}>
        <Box sx={{ ...modalStyle, width: 480 }}>
          <DelaiCreate
            ActionId={idaction}
            actionData={actionData}
            onClose={() => setOpenCreate(false)}
            onSuccess={() => {
              dispatch(getDelaisByActionId(idaction))
              onDataChange?.()
            }}
          />
        </Box>
      </Modal>

      <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
        <Box sx={modalStyle}>
          <h4 style={{ margin: '0 0 16px' }}>{t('popups.changeState.title')}</h4>
          <Stack spacing={2}>
            <Selectetat
              value={Etat}
              onChange={(e: SelectChangeEvent<string>) =>
                setEtat(String(e.target.value))
              }
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <DetailButton onClick={handleUpdateEtat}>
                {t('popups.changeState.updateButton')}
              </DetailButton>
              <DetailButton
                variant="ghost"
                onClick={() => setOpenEditModal(false)}
              >
                {t('buttons.cancel')}
              </DetailButton>
            </div>
          </Stack>
        </Box>
      </Modal>
    </>
  )
}

export default DelaiListe
